/**
 * Detail d'un ticket de support
 * Design System v2 (Volvo-inspired) - Matching CreditNotes style
 */

import { useState } from 'react';
import {
  ArrowLeft,
  Send,
  User,
  Clock,
  MessageSquare,
  CheckCircle,
  Headphones,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useTicket, useAddTicketMessage, useCloseTicket, useReopenTicket } from '../hooks/useTickets';
import { TicketStatusBadge } from './TicketStatusBadge';
import { TicketPriorityBadge } from './TicketPriorityBadge';

interface TicketDetailProps {
  ticketId: string;
  onBack?: () => void;
}

export function TicketDetail({ ticketId, onBack }: TicketDetailProps) {
  const { t } = useTranslation();
  const [newMessage, setNewMessage] = useState('');

  const { data: ticket, isLoading, error } = useTicket(ticketId);
  const addMessage = useAddTicketMessage();
  const closeTicket = useCloseTicket();
  const reopenTicket = useReopenTicket();

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    await addMessage.mutateAsync({
      ticketId,
      content: newMessage,
    });

    setNewMessage('');
  };

  const handleCloseTicket = async () => {
    await closeTicket.mutateAsync(ticketId);
  };

  const handleReopenTicket = async () => {
    await reopenTicket.mutateAsync(ticketId);
  };

  if (isLoading) {
    return (
      <div className="px-8 pt-8 pb-12">
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-12 text-center">
          <div className="animate-spin w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full mx-auto" />
          <p className="text-gray-600 mt-4">{t('support.loadingTicket')}</p>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="px-8 pt-8 pb-12">
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-12 text-center">
          <p className="text-red-500 mb-4">{t('support.ticketNotFound')}</p>
          <button
            onClick={onBack}
            className="px-4 py-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-xl transition-all duration-300"
          >
            {t('support.backToList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button
          onClick={onBack}
          className="p-2.5 bg-white/40 border border-white/50 rounded-xl text-gray-600
                     hover:bg-white hover:text-teal-600 hover:border-teal-200 hover:shadow-lg hover:shadow-teal-100/50
                     active:scale-95 transition-all duration-300"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-teal-100 rounded-xl">
              <Headphones className="w-5 h-5 text-teal-600" />
            </div>
            <span className="text-sm text-gray-500 font-mono bg-gray-100/80 px-2 py-0.5 rounded-lg">
              #{ticket.number || ticket.id.slice(0, 8)}
            </span>
            <TicketStatusBadge status={ticket.status} />
            <TicketPriorityBadge priority={ticket.priority} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{ticket.subject}</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description Card */}
          <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-teal-500" />
              {t('support.detail.description')}
            </h3>
            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
              {ticket.description}
            </p>
          </div>

          {/* Messages Card */}
          <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden">
            <div className="p-4 border-b border-white/30 bg-gray-50/30">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-teal-500" />
                {t('support.detail.conversationCount', { count: ticket.messages?.length || 0 })}
              </h3>
            </div>

            {/* Messages List */}
            <div className="divide-y divide-white/20 max-h-96 overflow-y-auto">
              {ticket.messages?.map((message: any) => (
                <div
                  key={message.id}
                  className="p-4 bg-white/10 hover:bg-white/30 transition-all duration-300"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 text-white
                                    flex items-center justify-center text-sm font-medium flex-shrink-0 shadow-sm">
                      {message.Author?.username?.charAt(0).toUpperCase() || t('support.detail.user').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">
                          {message.Author?.username || t('support.detail.user')}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDistanceToNow(new Date(message.createdAt), {
                            addSuffix: true,
                            locale: fr,
                          })}
                        </span>
                      </div>
                      <p className="text-gray-700 mt-1.5 whitespace-pre-wrap">
                        {message.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {(!ticket.messages || ticket.messages.length === 0) && (
                <div className="p-8 text-center">
                  <div className="w-12 h-12 bg-gray-100/80 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <MessageSquare className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-sm">{t('support.detail.noMessages')}</p>
                </div>
              )}
            </div>

            {/* New Message Form */}
            <div className="p-4 border-t border-white/30 bg-gray-50/30">
              <div className="flex gap-3">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t('support.detail.replyPlaceholder')}
                  className="flex-1 px-4 py-3 bg-white/40 border border-white/50 rounded-xl resize-none
                             focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white
                             hover:bg-white/60 hover:border-teal-200 transition-all duration-300"
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.metaKey) {
                      handleSendMessage();
                    }
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || addMessage.isPending}
                  className="px-4 py-3 bg-teal-600 text-white rounded-xl
                             hover:bg-teal-700 hover:shadow-lg hover:shadow-teal-200/50 hover:scale-[1.02]
                             active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                             transition-all duration-300 self-end"
                >
                  {addMessage.isPending ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {t('support.detail.cmdEnterToSend')}
              </p>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Info Card */}
          <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">{t('support.detail.info')}</h3>

            <div className="space-y-4">
              <div className="flex items-center gap-3 group p-2 rounded-xl hover:bg-white/30 transition-all duration-300">
                <div className="w-9 h-9 bg-gray-100/80 rounded-xl flex items-center justify-center
                                group-hover:bg-teal-100 transition-colors duration-300">
                  <User className="w-4 h-4 text-gray-500 group-hover:text-teal-600 transition-colors duration-300" />
                </div>
                <div>
                  <div className="text-xs text-gray-500">{t('support.detail.createdBy')}</div>
                  <div className="text-sm font-medium text-gray-900">
                    {ticket.createdBy?.name || t('support.detail.you')}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 group p-2 rounded-xl hover:bg-white/30 transition-all duration-300">
                <div className="w-9 h-9 bg-gray-100/80 rounded-xl flex items-center justify-center
                                group-hover:bg-teal-100 transition-colors duration-300">
                  <Clock className="w-4 h-4 text-gray-500 group-hover:text-teal-600 transition-colors duration-300" />
                </div>
                <div>
                  <div className="text-xs text-gray-500">{t('support.detail.createdAt')}</div>
                  <div className="text-sm font-medium text-gray-900">
                    {format(new Date(ticket.createdAt), 'dd MMM yyyy HH:mm', { locale: fr })}
                  </div>
                </div>
              </div>

              {ticket.assignedTo && (
                <div className="flex items-center gap-3 p-2 rounded-xl bg-teal-50/50">
                  <div className="w-9 h-9 bg-teal-100 rounded-xl flex items-center justify-center">
                    <User className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">{t('support.detail.assignedTo')}</div>
                    <div className="text-sm font-medium text-gray-900">
                      {ticket.assignedTo.name}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status Guide */}
          <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">{t('support.statusGuide.title')}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/30 transition-all duration-300">
                <div className="w-3 h-3 rounded-full bg-blue-500 shadow-sm shadow-blue-200" />
                <span className="text-gray-600">{t('support.statusGuide.open')}</span>
              </div>
              <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/30 transition-all duration-300">
                <div className="w-3 h-3 rounded-full bg-yellow-500 shadow-sm shadow-yellow-200" />
                <span className="text-gray-600">{t('support.statusGuide.inProgress')}</span>
              </div>
              <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/30 transition-all duration-300">
                <div className="w-3 h-3 rounded-full bg-green-500 shadow-sm shadow-green-200" />
                <span className="text-gray-600">{t('support.statusGuide.resolved')}</span>
              </div>
              <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/30 transition-all duration-300">
                <div className="w-3 h-3 rounded-full bg-gray-400 shadow-sm shadow-gray-200" />
                <span className="text-gray-600">{t('support.statusGuide.closed')}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          {ticket.status === 'RESOLVED' && (
            <div className="bg-green-50/50 backdrop-blur-xl rounded-3xl border border-green-200/50 p-5 space-y-4">
              <div className="flex items-center gap-3 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">{t('support.actions.ticketResolved')}</span>
              </div>
              <p className="text-sm text-green-600">
                {t('support.actions.resolvedDescription')}
              </p>
              <button
                onClick={handleCloseTicket}
                disabled={closeTicket.isPending}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl
                           hover:bg-green-700 hover:shadow-lg hover:shadow-green-200/50
                           active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-300"
              >
                {closeTicket.isPending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>{t('support.actions.closeTicket')}</span>
                  </>
                )}
              </button>
            </div>
          )}

          {ticket.status === 'CLOSED' && (
            <div className="bg-gray-50/50 backdrop-blur-xl rounded-3xl border border-gray-200/50 p-5 space-y-4">
              <div className="flex items-center gap-3 text-gray-700">
                <XCircle className="w-5 h-5" />
                <span className="font-medium">{t('support.actions.ticketClosed')}</span>
              </div>
              <p className="text-sm text-gray-600">
                {t('support.actions.closedDescription')}
              </p>
              <button
                onClick={handleReopenTicket}
                disabled={reopenTicket.isPending}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-xl
                           hover:bg-teal-700 hover:shadow-lg hover:shadow-teal-200/50
                           active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-300"
              >
                {reopenTicket.isPending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    <span>{t('support.actions.reopenTicket')}</span>
                  </>
                )}
              </button>
            </div>
          )}

          {(ticket.status === 'OPEN' || ticket.status === 'IN_PROGRESS' || ticket.status === 'WAITING_USER') && (
            <div className="bg-blue-50/50 backdrop-blur-xl rounded-3xl border border-blue-200/50 p-5">
              <div className="flex items-center gap-3 text-blue-700">
                <MessageSquare className="w-5 h-5" />
                <span className="font-medium">{t('support.actions.ticketInProgress')}</span>
              </div>
              <p className="text-sm text-blue-600 mt-2">
                {t('support.actions.inProgressDescription')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
